import type { Animal, DrawStep } from "./animals";
import { nameStep } from "./handwriting";

// "Shape Building" — the toddler-first section. Where the rest of the site
// teaches multi-step pictures of real things (dogs, houses, pop stars), this
// section teaches the plain geometry underneath them: one big, bold shape at a
// time. Toddlers don't understand perspective yet — they understand round,
// pointy, and straight — so each drawing is a single giant shape that fills the
// canvas, coloured in, and finished by writing the shape's NAME letter by
// letter (so the word and the shape are learned together).
//
// Every shape lives in the same 200×200 canvas as the rest of the app, centred
// high (around y≈88) so there's room beneath for the name lettering.

const OUTLINE = "#3a3a55";

const n = (x: number) => Math.round(x * 10) / 10;

/** A circle as two arcs (kept a single path so it draws and fills cleanly). */
const circle = (cx: number, cy: number, r: number) =>
  `M ${n(cx - r)},${cy} a ${r},${r} 0 1,0 ${2 * r},0 a ${r},${r} 0 1,0 ${-2 * r},0`;

/** An ellipse (oval) as two arcs. */
const ellipse = (cx: number, cy: number, rx: number, ry: number) =>
  `M ${n(cx - rx)},${cy} a ${rx},${ry} 0 1,0 ${2 * rx},0 a ${rx},${ry} 0 1,0 ${-2 * rx},0`;

/** An axis-aligned rectangle from its corners. */
const rect = (x0: number, y0: number, x1: number, y1: number) =>
  `M ${n(x0)},${n(y0)} L ${n(x1)},${n(y0)} L ${n(x1)},${n(y1)} L ${n(x0)},${n(y1)} Z`;

/** A regular polygon (triangle, diamond, pentagon…) centred at (cx,cy). */
const poly = (cx: number, cy: number, r: number, sides: number, rotDeg = -90) => {
  const pts: string[] = [];
  for (let i = 0; i < sides; i++) {
    const a = ((rotDeg + (i * 360) / sides) * Math.PI) / 180;
    pts.push(`${n(cx + r * Math.cos(a))},${n(cy + r * Math.sin(a))}`);
  }
  return "M " + pts.join(" L ") + " Z";
};

/** A five-pointed star (outer radius rO, inner radius rI). */
const star5 = (cx: number, cy: number, rO: number, rI: number, rotDeg = -90) => {
  const pts: string[] = [];
  for (let i = 0; i < 5; i++) {
    const ao = ((rotDeg + i * 72) * Math.PI) / 180;
    pts.push(`${n(cx + rO * Math.cos(ao))},${n(cy + rO * Math.sin(ao))}`);
    const ai = ((rotDeg + i * 72 + 36) * Math.PI) / 180;
    pts.push(`${n(cx + rI * Math.cos(ai))},${n(cy + rI * Math.sin(ai))}`);
  }
  return "M " + pts.join(" L ") + " Z";
};

/** A crescent (lune): the outer circle (cx,cy,R) minus a bite circle of radius
 *  `br` centred `dx` to the right. Opens rightward, like a waxing moon. */
const crescent = (cx: number, cy: number, R: number, dx: number, br: number) => {
  const a = (dx * dx + R * R - br * br) / (2 * dx);
  const h = Math.sqrt(Math.max(0, R * R - a * a));
  const px = cx + a;
  return `M ${n(px)},${n(cy - h)} A ${R},${R} 0 1,0 ${n(px)},${n(cy + h)} A ${br},${br} 0 0,1 ${n(px)},${n(cy - h)} Z`;
};

// A plump heart centred around (100, 88).
const HEART =
  "M 100,150 C 52,114 40,86 40,66 C 40,46 58,38 74,44 C 86,48 96,58 100,70 C 104,58 114,48 126,44 C 142,38 160,46 160,66 C 160,86 148,114 100,150 Z";

/**
 * Build one shape drawing: draw the big bold outline, colour it in, then write
 * the shape's name. A single path per shape keeps the pen-stroke clean and the
 * fill crisp — exactly what a very young child can follow.
 */
function shape(
  id: string,
  name: string,
  emoji: string,
  path: string,
  fill: string,
  drawHint: string,
  fact: string,
): Animal {
  const steps: DrawStep[] = [
    // Bold guide line — chunky so it reads for the youngest hands.
    { hint: drawHint, color: OUTLINE, strokes: [path], strokeWidth: 6 },
    {
      hint: `Now colour your ${name.toLowerCase()} in! 🖍️`,
      color: OUTLINE,
      strokes: [],
      fills: [{ d: path, color: fill }],
      strokeWidth: 6,
    },
    nameStep(name, { baseline: 190, height: 20 }),
  ];
  return { id, name, emoji, viewBox: "0 0 200 200", color: OUTLINE, fact, steps };
}

export const shapes: Animal[] = [
  shape(
    "shape-circle",
    "Circle",
    "⭕",
    circle(100, 88, 62),
    "#ff6b6b",
    "This is a CIRCLE! Draw one big round circle ⭕",
    "A circle is perfectly round — it has no corners at all! The sun and the moon are circles. ☀️",
  ),
  shape(
    "shape-square",
    "Square",
    "🟦",
    rect(38, 26, 162, 150),
    "#4dabf7",
    "This is a SQUARE! Draw 4 sides that are all the same 🟦",
    "A square has 4 sides that are all exactly the same length!",
  ),
  shape(
    "shape-triangle",
    "Triangle",
    "🔺",
    "M 100,28 L 164,150 L 36,150 Z",
    "#51cf66",
    "This is a TRIANGLE! Draw 3 straight sides 🔺",
    "A triangle has 3 sides and 3 pointy corners!",
  ),
  shape(
    "shape-rectangle",
    "Rectangle",
    "▭",
    rect(28, 52, 172, 132),
    "#ffa94d",
    "This is a RECTANGLE! 2 long sides and 2 short sides ▭",
    "A rectangle has 4 corners, with 2 long sides and 2 short sides — like a door! 🚪",
  ),
  shape(
    "shape-star",
    "Star",
    "⭐",
    star5(100, 92, 64, 30),
    "#ffd43b",
    "This is a STAR! Draw 5 pointy points ⭐",
    "A star has 5 points that twinkle in the night sky! ✨",
  ),
  shape(
    "shape-heart",
    "Heart",
    "❤️",
    HEART,
    "#ff8cc3",
    "This is a HEART! Draw two bumps and a point ❤️",
    "A heart is the shape we use to show love! 💕",
  ),
  shape(
    "shape-oval",
    "Oval",
    "🥚",
    ellipse(100, 90, 68, 44),
    "#b197fc",
    "This is an OVAL! Like a circle, but stretched 🥚",
    "An oval is like a circle that got gently squished — an egg is an oval! 🥚",
  ),
  shape(
    "shape-diamond",
    "Diamond",
    "💎",
    poly(100, 88, 64, 4),
    "#38d9d9",
    "This is a DIAMOND! A square standing on its point 💎",
    "A diamond is a square balancing on one of its corners! 💎",
  ),
  shape(
    "shape-crescent",
    "Crescent",
    "🌙",
    crescent(86, 88, 64, 40, 56),
    "#ffe066",
    "This is a CRESCENT! The curvy shape of the moon 🌙",
    "A crescent is the curvy shape the moon makes at night! 🌙",
  ),
  shape(
    "shape-pentagon",
    "Pentagon",
    "⬠",
    poly(100, 92, 64, 5),
    "#f783ac",
    "This is a PENTAGON! Draw 5 straight sides ⬠",
    "A pentagon has 5 straight sides — a little house is shaped a bit like one! 🏠",
  ),
  shape(
    "shape-hexagon",
    "Hexagon",
    "⬡",
    poly(100, 90, 62, 6, -60), // flat-top hexagon (points on the sides)
    "#f06595",
    "This is a HEXAGON! Draw 6 straight sides ⬡",
    "A hexagon has 6 sides — bees build their honeycomb out of hexagons! 🐝",
  ),
  shape(
    "shape-octagon",
    "Octagon",
    "🛑",
    poly(100, 90, 64, 8, -67.5), // flat-top octagon, like a stop sign
    "#fcc419",
    "This is an OCTAGON! Draw 8 straight sides 🛑",
    "An octagon has 8 sides — a red STOP sign is an octagon! 🛑",
  ),
  shape(
    "shape-parallelogram",
    "Parallelogram",
    "▱",
    "M 62,54 L 166,54 L 138,128 L 34,128 Z", // a rectangle leaning to the right
    "#339af0",
    "This is a PARALLELOGRAM! A rectangle that leans over ▱",
    "A parallelogram is like a rectangle that leaned over to one side!",
  ),
  shape(
    "shape-trapezium",
    "Trapezium",
    "⏢",
    "M 66,54 L 134,54 L 170,132 L 30,132 Z", // narrow top, wide bottom
    "#94d82d",
    "This is a TRAPEZIUM! Narrow on top, wide at the bottom ⏢",
    "A trapezium has 4 sides, with a flat top and a flat bottom that aren't the same width!",
  ),
];

export function isShape(id: string): boolean {
  return shapes.some((s) => s.id === id);
}
